package diana.soleil.design.iraniancanadienbazzar.core.model

import kotlin.math.roundToInt

/**
 * Country-aware distance formatting (see `docs/US_EXPANSION_PLAN.md` §4). The heavy lifting — km↔mi
 * conversion and rounding — is centralized here so no screen ever hand-rolls "x km" vs "x mi" logic;
 * the UI only maps the resulting [DistanceDisplay.unit] to a localized string resource.
 *
 * Distances are computed in km everywhere ([distanceKm]); this converts to the unit the active metro's
 * [Country] uses — km/m for [Country.CA], mi/ft for [Country.US] — and picks the coarse (km/mi) vs fine
 * (m/ft) unit by proximity, matching the pre-US behaviour of reading sub-kilometre distances in metres.
 */
enum class DistanceUnit { KM, METRE, MILE, FOOT }

/** A distance ready to render: a formatted numeric [value] plus the [unit] whose string the UI resolves. */
data class DistanceDisplay(val value: String, val unit: DistanceUnit)

private const val KM_PER_MILE = 1.609344
private const val FEET_PER_MILE = 5280.0

/** One-decimal string with a trailing ".0" stripped ("2.4", "3"). */
private fun oneDecimal(x: Double): String {
    val tenths = (x * 10).roundToInt()
    return if (tenths % 10 == 0) "${tenths / 10}" else "${tenths / 10}.${tenths % 10}"
}

/**
 * Format [km] for [country]. CA → "2.4 km" / "850 m"; US → "1.5 mi" / "300 ft". The fine unit (m/ft)
 * kicks in below 1 km / below 0.1 mi respectively.
 */
fun formatDistance(km: Double, country: Country): DistanceDisplay =
    if (country.usesMiles) {
        val miles = km / KM_PER_MILE
        if (miles < 0.1) {
            DistanceDisplay((miles * FEET_PER_MILE).roundToInt().toString(), DistanceUnit.FOOT)
        } else {
            DistanceDisplay(oneDecimal(miles), DistanceUnit.MILE)
        }
    } else {
        if (km < 1.0) {
            DistanceDisplay((km * 1000).roundToInt().toString(), DistanceUnit.METRE)
        } else {
            DistanceDisplay(oneDecimal(km), DistanceUnit.KM)
        }
    }
